import type { ErrorEvent } from "@sentry/react";

import { FIRST_PARTY_REQUEST_TARGETS } from "./first-party-request-targets";
import { hasUnresolvedPathSegment } from "./http-client-fingerprint";
import { USER_VERDICT_STATUSES } from "./user-verdict-statuses";

// An axios error that escapes a route loader or a query is captured as a
// regular exception, outside the reach of `httpClientIntegration` and its
// status range. Two families of first-party responses are suppressed here:
//
// - 5xx: a backend we own already reports the failure to its own Sentry
//   project — the frontend copy is a duplicate nobody triages
//   (PLATFORM-LCM-5SA was a MySQL lock wait timeout in `get-users`).
// - user-verdict 4xx (see `user-verdict-statuses.ts`): the backend rejecting
//   what the user did; the UI shows it and Sentry does not need a copy —
//   unless the request was built from an unresolved parameter (a literal
//   `undefined` in the path), which is a frontend defect worth keeping.
//
// Status and URL come from `extraErrorDataIntegration`, which copies the
// error's own properties into `contexts[error.name]`. The axios error is not
// necessarily the outermost exception (an ErrorBoundary rethrows around it),
// so the contexts are scanned for the axios shape instead of reading
// `exception.values[0]`.
export function isSuppressedApiError(event: ErrorEvent): boolean {
  return containsSuppressedApiError(event.contexts, 0);
}

// An axios error wrapped in a cause chain lands nested rather than at the top
// of `contexts` (the observed event carried `cause` and `response` sub-objects),
// so the whole tree is walked — bounded like `scrub-sensitive-data`, since
// `normalizeDepth` already caps what Sentry serialises.
const MAX_DEPTH = 8;

function containsSuppressedApiError(
  container: unknown,
  depth: number,
): boolean {
  if (
    depth >= MAX_DEPTH ||
    typeof container !== "object" ||
    container === null
  ) {
    return false;
  }
  const axiosError = asAxiosErrorContext(container);
  if (axiosError !== null && isSuppressedResponse(axiosError)) {
    return true;
  }
  return Object.values(container).some((value) =>
    containsSuppressedApiError(value, depth + 1),
  );
}

function isSuppressedResponse({ status, url }: AxiosErrorContext): boolean {
  if (!isFirstPartyUrl(url)) {
    return false;
  }
  if (status >= 500) {
    return true;
  }
  return USER_VERDICT_STATUSES.has(status) && !hasUnresolvedPathSegment(url);
}

type AxiosErrorContext = {
  status: number;
  url: string;
};

function asAxiosErrorContext(context: unknown): AxiosErrorContext | null {
  if (typeof context !== "object" || context === null) {
    return null;
  }
  if (!("isAxiosError" in context) || context.isAxiosError !== true) {
    return null;
  }
  if (!("status" in context) || typeof context.status !== "number") {
    return null;
  }
  if (!("config" in context)) {
    return null;
  }
  const { config } = context;
  if (typeof config !== "object" || config === null || !("url" in config)) {
    return null;
  }
  const { url } = config;
  if (typeof url !== "string") {
    return null;
  }
  return { status: context.status, url: resolveRequestUrl(config, url) };
}

// Orval clients keep `config.url` as the path alone and carry the host in
// `config.baseURL`, so testing `config.url` matched none of the host patterns
// and this filter dropped nothing for any generated client (observed on
// PLATFORM-LCM-516: `/v2/tracking-manager/file/upload` against
// `https://lcm.api.makesuccess.io`). Join the two before matching, and keep the
// bare path when there is no `baseURL` — that is the same-origin php backend,
// which the targets match on the relative form.
function resolveRequestUrl(config: object, url: string): string {
  // Axios ignores `baseURL` for an absolute url, so concatenating would leave a
  // first-party host in front of a third-party request and drop it by mistake.
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) {
    return url;
  }
  if (!("baseURL" in config)) {
    return url;
  }
  const { baseURL } = config;
  if (typeof baseURL !== "string" || baseURL === "") {
    return url;
  }
  return `${baseURL.replace(/\/$/, "")}${url}`;
}

// The php backend is reached through a relative url, so the raw string is
// tested rather than a parsed URL — same contract as `failedRequestTargets`.
function isFirstPartyUrl(url: string): boolean {
  return FIRST_PARTY_REQUEST_TARGETS.some((target) => target.test(url));
}
